import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

/** Rótulo do eixo Y virando link de verdade (`<a>` dentro do SVG) — assim dá pra abrir os
 * tickets em andamento do cliente em nova aba com ctrl/cmd+clique, não só clique simples. */
function ClientNameTick({
  x,
  y,
  payload,
  clients,
}: {
  x: number;
  y: number;
  payload: { value: string };
  clients: Array<{ label: string; clientId?: string }>;
}) {
  const item = clients.find((c) => c.label === payload.value);
  if (!item?.clientId) {
    return (
      <text x={x} y={y} dy={4} textAnchor="end" fontSize={11} fill="var(--muted-foreground)">
        {payload.value}
      </text>
    );
  }
  return (
    <a href={`/tickets?clientId=${encodeURIComponent(item.clientId)}&status=in_progress`}>
      <text
        x={x}
        y={y}
        dy={4}
        textAnchor="end"
        fontSize={11}
        fill="hsl(var(--primary))"
        style={{ textDecoration: "underline", cursor: "pointer" }}
      >
        {payload.value}
      </text>
    </a>
  );
}

const DEFAULT_VOLUME = [
  { name: "Dom", tickets: 0 },
  { name: "Seg", tickets: 0 },
  { name: "Ter", tickets: 0 },
  { name: "Qua", tickets: 0 },
  { name: "Qui", tickets: 0 },
  { name: "Sex", tickets: 0 },
  { name: "Sab", tickets: 0 },
];

interface DashboardChartsProps {
  volumeByDay?: Array<{ name: string; tickets: number }>;
  topTicketTags?: Array<{ tag: string; count: number }>;
  topClientsByTickets?: Array<{ clientId: string; name: string; count: number }>;
  topAgentsByTickets?: Array<{ name: string; count: number }>;
  ticketsByStatus?: Array<{ status: string; label: string; count: number }>;
  ticketsByChannel?: Array<{ channel: string; label: string; count: number }>;
  isLoading?: boolean;
}

export default function DashboardCharts({
  volumeByDay,
  topTicketTags,
  topClientsByTickets,
  topAgentsByTickets,
  ticketsByStatus,
  ticketsByChannel,
  isLoading,
}: DashboardChartsProps) {
  const volume = volumeByDay?.length ? volumeByDay : DEFAULT_VOLUME;
  const tags = (topTicketTags || []).map((r) => ({
    label: r.tag.length > 18 ? `${r.tag.slice(0, 18)}…` : r.tag,
    fullTag: r.tag,
    count: r.count,
  }));
  const clients = (topClientsByTickets || []).map((r) => ({
    label: r.name.length > 16 ? `${r.name.slice(0, 16)}…` : r.name,
    fullName: r.name,
    count: r.count,
    clientId: r.clientId,
  }));
  const agents = (topAgentsByTickets || []).map((r) => ({
    label: r.name.length > 16 ? `${r.name.slice(0, 16)}…` : r.name,
    fullName: r.name,
    count: r.count,
  }));

  // Uma única barra empilhada — "parte do todo" (total de tickets), não comparação entre categorias
  // soltas. Ordem escolhida (andamento, aberto, resolvido) pra manter os pares de cor adjacentes
  // seguros pra daltonismo (validado com a skill de dataviz); o rótulo de cada segmento não depende
  // dessa ordem.
  const statusRow = [
    (ticketsByStatus || []).reduce(
      (acc, r) => ({ ...acc, [r.status]: r.count }),
      { name: "Tickets" } as Record<string, number | string>,
    ),
  ];
  const channelRow = [
    (ticketsByChannel || []).reduce(
      (acc, r) => ({ ...acc, [r.channel]: r.count }),
      { name: "Tickets" } as Record<string, number | string>,
    ),
  ];

  const tooltipStyle = {
    borderRadius: "0.75rem",
    border: "1px solid var(--border)",
    backgroundColor: "var(--card)",
    boxShadow: "0 4px 24px 0 rgba(0,0,0,0.08)",
    fontSize: "13px",
  };

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <div key={i} className="rounded-2xl border border-border/50 bg-card p-6 opacity-0 animate-fadeIn animate-delay-200">
            <div className="h-4 bg-muted rounded-full w-1/2 mb-4 animate-pulse" />
            <div className="h-64 bg-muted/30 rounded-xl animate-pulse" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="rounded-2xl border border-border/50 bg-card p-6 shadow-sm opacity-0 animate-fadeIn animate-delay-200">
        <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-4">
          Tickets por dia — últimos 7 dias
        </h3>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={volume} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted/50" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
              <YAxis allowDecimals={false} width={28} tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "var(--muted)", opacity: 0.4, radius: 6 }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="tickets" name="Tickets" fill="hsl(var(--primary))" radius={[6, 6, 0, 0]} maxBarSize={36} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="rounded-2xl border border-border/50 bg-card p-6 shadow-sm opacity-0 animate-fadeIn animate-delay-200">
        <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-4">
          Tags mais usadas
        </h3>
        <div className="h-64">
          {tags.length ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                layout="vertical"
                data={tags}
                margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted/50" horizontal={false} />
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
                <YAxis
                  type="category"
                  dataKey="label"
                  width={90}
                  tick={{ fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(v) => String(v)}
                />
                <Tooltip
                  contentStyle={tooltipStyle}
                  formatter={(value: number) => [value, "Tickets"]}
                  labelFormatter={(_, payload) =>
                    payload?.[0]?.payload?.fullTag ? String(payload[0].payload.fullTag) : ""
                  }
                  cursor={{ fill: "var(--muted)", opacity: 0.4 }}
                />
                <Bar dataKey="count" name="Tickets" fill="hsl(var(--primary) / 0.75)" radius={[0, 6, 6, 0]} maxBarSize={28} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex h-full items-center justify-center">
              <p className="text-sm text-muted-foreground">Nenhuma tag nos tickets ainda</p>
            </div>
          )}
        </div>
      </div>

      <div className="rounded-2xl border border-border/50 bg-card p-6 shadow-sm opacity-0 animate-fadeIn animate-delay-200">
        <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-4">
          Clientes com mais tickets
        </h3>
        <div className="h-64">
          {clients.length ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                layout="vertical"
                data={clients}
                margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted/50" horizontal={false} />
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
                <YAxis
                  type="category"
                  dataKey="label"
                  width={90}
                  tick={(props) => <ClientNameTick {...props} clients={clients} />}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip
                  contentStyle={tooltipStyle}
                  formatter={(value: number) => [value, "Tickets"]}
                  labelFormatter={(_, payload) =>
                    payload?.[0]?.payload?.fullName ? String(payload[0].payload.fullName) : ""
                  }
                  cursor={{ fill: "var(--muted)", opacity: 0.4 }}
                />
                <Bar dataKey="count" name="Tickets" fill="hsl(142 71% 45% / 0.8)" radius={[0, 6, 6, 0]} maxBarSize={28} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex h-full items-center justify-center">
              <p className="text-sm text-muted-foreground">Nenhum ticket vinculado a cliente</p>
            </div>
          )}
        </div>
      </div>

      <div className="rounded-2xl border border-border/50 bg-card p-6 shadow-sm opacity-0 animate-fadeIn animate-delay-300">
        <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-4">
          Tickets por status
        </h3>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart layout="vertical" data={statusRow} margin={{ top: 8, right: 8, left: 0, bottom: 0 }} barSize={48}>
              <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
              <YAxis type="category" dataKey="name" hide />
              <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "var(--muted)", opacity: 0.4 }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="in_progress" name="Em andamento" stackId="status" fill="hsl(var(--chart-status-progress))" radius={[6, 0, 0, 6]} />
              <Bar dataKey="open" name="Aberto" stackId="status" fill="hsl(var(--chart-status-open))" />
              <Bar dataKey="resolved" name="Resolvido" stackId="status" fill="hsl(var(--chart-status-resolved))" radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="rounded-2xl border border-border/50 bg-card p-6 shadow-sm opacity-0 animate-fadeIn animate-delay-300">
        <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-4">
          Tickets por canal
        </h3>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart layout="vertical" data={channelRow} margin={{ top: 8, right: 8, left: 0, bottom: 0 }} barSize={48}>
              <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
              <YAxis type="category" dataKey="name" hide />
              <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "var(--muted)", opacity: 0.4 }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="whatsapp" name="WhatsApp" stackId="channel" fill="hsl(var(--chart-channel-whatsapp))" radius={[6, 0, 0, 6]} />
              <Bar dataKey="web" name="Portal" stackId="channel" fill="hsl(var(--chart-channel-web))" />
              <Bar dataKey="outro" name="Outro" stackId="channel" fill="hsl(var(--muted-foreground) / 0.5)" radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="rounded-2xl border border-border/50 bg-card p-6 shadow-sm opacity-0 animate-fadeIn animate-delay-300">
        <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-4">
          Agentes com mais tickets
        </h3>
        <div className="h-64">
          {agents.length ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                layout="vertical"
                data={agents}
                margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted/50" horizontal={false} />
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
                <YAxis
                  type="category"
                  dataKey="label"
                  width={90}
                  tick={{ fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip
                  contentStyle={tooltipStyle}
                  formatter={(value: number) => [value, "Tickets"]}
                  labelFormatter={(_, payload) =>
                    payload?.[0]?.payload?.fullName ? String(payload[0].payload.fullName) : ""
                  }
                  cursor={{ fill: "var(--muted)", opacity: 0.4 }}
                />
                <Bar dataKey="count" name="Tickets" fill="hsl(var(--chart-agent))" radius={[0, 6, 6, 0]} maxBarSize={28} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex h-full items-center justify-center">
              <p className="text-sm text-muted-foreground">Nenhum ticket atribuído a um agente</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
